import React from 'react';
import { Group, Tooltip, UnstyledButton } from '@mantine/core';
import { IconRoute } from '@tabler/icons-react';
import { GANTT_TOOLBAR_BUTTON_RADIUS, GANTT_TOOLBAR_CONTROL_BORDER } from './ganttToolbarStyles';

export interface GanttLiveBadgeProps {
  enabled: boolean;
  /** When true, show the Critical path icon next to the Live pill. */
  criticalPathOnly?: boolean;
  /** Toggle Live on/off (or open related controls). */
  onClick?: () => void;
  /** Optional: jump to critical-path controls when the critical chip is clicked. */
  onCriticalPathClick?: () => void;
}

/** Compact Live Re-Scheduling status pill for the primary toolbar. */
export function GanttLiveBadge({
  enabled,
  criticalPathOnly = false,
  onClick,
  onCriticalPathClick,
}: GanttLiveBadgeProps) {
  const label = enabled ? 'Live' : 'Live off';
  return (
    <Group gap={6} wrap="nowrap" align="center">
      <Tooltip
        label={
          enabled
            ? 'Live re-scheduling is on. Linked tasks update when you edit dates or dependencies. Click to turn off.'
            : 'Live re-scheduling is off. Click to turn on, or use Recalculate schedule.'
        }
        multiline
        w={280}
        withArrow
      >
        <UnstyledButton
          type="button"
          onClick={onClick}
          aria-label={enabled ? 'Live re-scheduling on' : 'Live re-scheduling off'}
          className="gantt-live-badge"
          data-enabled={enabled ? 'true' : 'false'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 28,
            padding: '0 10px',
            borderRadius: GANTT_TOOLBAR_BUTTON_RADIUS,
            border: GANTT_TOOLBAR_CONTROL_BORDER,
            background: enabled
              ? 'color-mix(in srgb, var(--mantine-color-teal-6) 14%, var(--mantine-color-body))'
              : 'var(--mantine-color-body)',
            color: enabled ? 'var(--mantine-color-teal-8)' : 'var(--mantine-color-dimmed)',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            cursor: onClick ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
          }}
        >
          <span className="gantt-live-badge__indicator" aria-hidden>
            <span className="gantt-live-badge__ping" />
            <span className="gantt-live-badge__dot" />
          </span>
          {label}
        </UnstyledButton>
      </Tooltip>

      {criticalPathOnly ? (
        <Tooltip
          label="Critical path only is on. Showing critical tasks and their ancestors. Change this in Analysis."
          multiline
          w={260}
          withArrow
        >
          <UnstyledButton
            type="button"
            onClick={onCriticalPathClick ?? onClick}
            aria-label="Critical path only filter on"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: GANTT_TOOLBAR_BUTTON_RADIUS,
              border: GANTT_TOOLBAR_CONTROL_BORDER,
              background:
                'color-mix(in srgb, var(--mantine-color-orange-6) 16%, var(--mantine-color-body))',
              color: 'var(--mantine-color-orange-8)',
              cursor: onCriticalPathClick || onClick ? 'pointer' : 'default',
            }}
          >
            <IconRoute size={14} stroke={1.75} />
          </UnstyledButton>
        </Tooltip>
      ) : null}
    </Group>
  );
}
